import { PureComponent, useState, useEffect } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { useDispatch, connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Form, Icon, Modal, Radio, Segment } from 'semantic-ui-react';
import Alert from 'reactstrap/lib/Alert';
import ShowMoreText from 'react-show-more-text';
import { Field, FieldArray, Formik } from 'formik';

import { CustomTable, CustomTooltip, SlideoutPanel } from '../../../components';

import {
  cloneDeep,
  difference,
  find,
  findIndex,
  forEach,
  get,
  isEmpty,
  map,
} from '../../../utils/lodash';

import {
  createProjectEvidence,
  deleteObjectiveEvidence,
  getEvidenceMaterialTypes,
  getEvidenceTypes,
  updateProjectEvidence,
} from '../../../store/actions';

import { getPageDisable } from '../../../utils';
import { CheckboxComp } from '../../../components/form/checkboxes/checkBoxGroup';

import {
  evidenceInitialValues,
  evidenceValidationSchema,
} from '../../../validations/evidenceValidation';

import {
  CustomDropdown,
  CustomInput,
  CustomTextArea,
} from '../../../components/form';

import { EvidenceLibrary } from './evidenceLibrary';

import {
  EVIDENCE_TYPE,
  EVIDENCE_TYPE_FIELD_CONFIG,
  AUTHOR_REQUIRED_TYPES,
  LINK_REQUIRED_TYPES,
  isLegacyDataType,
  getEvidenceDisplayTitle,
  getEvidenceTypeLine,
} from '../../../utils/evidenceTypes';

import './activity.scss';

const EvidenceForm = (props) => {
  const { t } = props;
  const dispatch = useDispatch();
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState([]);
  // Initialised synchronously from props (rather than via a mount effect) so
  // an existing record's type — and therefore the modal's step2 width — is
  // correct on the very first render. Otherwise the first paint briefly
  // shows step1/50vw before the effect runs, and the width transition to
  // 75vw plays visibly (inconsistently, depending on render/paint timing).
  const [evidenceType, setEvidenceType] = useState(() =>
    props.evidence ? props.evidence.evidenceType : 0
  );
  const [evidenceMessage, setEvidenceMessage] = useState('');
  const [stepDirection, setStepDirection] = useState('forward');
  const [hoveredMessage, setHoveredMessage] = useState('');

  useEffect(() => {
    const { evidence, helpTips } = props;

    let helpMessage = '';
    if (evidence) {
      helpMessage = find(helpTips, {
        title: get(evidence, 'evidenceTypeData.evidenceType'),
      });
    }

    setEvidenceMessage(helpMessage ? helpMessage.content : '');

    setEvidence();
  }, []);

  useEffect(() => {
    if (props.evidence) {
      setEvidence();
    }
  }, [props.evidence]);

  useEffect(() => {
    const typeId = Number(evidenceType);
    const fieldConfig = EVIDENCE_TYPE_FIELD_CONFIG[typeId];
    if (fieldConfig && fieldConfig.showMaterialType) {
      dispatch(getEvidenceMaterialTypes({ evidenceTypeId: typeId }));
    }
  }, [evidenceType]);

  const setEvidence = async () => {
    const { evidence } = props;
    const { currentActivity } = props.activity;

    let defaultEvidence = [];
    let options = [];
    if (evidence && evidence.objective) {
      defaultEvidence = map(evidence.objective, (item) => {
        return item.id;
      });
    }
    if (currentActivity.objective) {
      options = map(currentActivity.objective, (item, i) => {
        return {
          key: item.id,
          text: `${item.outcome.title}`,
          label: `${item.outcome.title}`,
          value: item.id,
        };
      });
    }

    setOptions(options);
    setValue(defaultEvidence);
  };

  async function handleValidSubmit(values) {
    try {
      const { isNew, evidence, activity, toggle, isParent } = props;

      // `authors` is a form-only convenience field (see evidenceValidation.js) —
      // join it into the single `author` string the API actually expects.
      values.author = (values.authors || [])
        .map((name) => (name || '').trim())
        .filter((name) => name.length > 0)
        .join('; ');
      delete values.authors;

      if (values.content) values.content = values.content.trim();
      if (values.source) values.source = values.source.trim();

      if (!isLegacyData && fieldConfig) {
        if (!fieldConfig.showMaterialType) delete values.materialTypeId;
        if (!fieldConfig.showDoi) delete values.doi;
        if (!fieldConfig.showLink) delete values.link;
        if (!fieldConfig.showVisibilityToggle) delete values.visibleOnPlatform;
      } else {
        delete values.title;
        delete values.year;
        delete values.materialTypeId;
        delete values.doi;
        delete values.link;
        delete values.visibleOnPlatform;
      }

      if (isParent) {
        // Parent activities' evidence isn't linked to individual objectives —
        // no linkedObjectives/assignObjectives bookkeeping needed here.
        const dataSet = {
          ...values,
          projectId: activity.currentActivity.id,
        };
        if (isNew) {
          dispatch(createProjectEvidence('evidence', dataSet));
        } else {
          values.id = evidence.id;
          dispatch(
            updateProjectEvidence(
              'evidence',
              activity.currentActivity.id,
              evidence.id,
              dataSet
            )
          );
        }
      } else if (activity.currentActivity.objective) {
        const objective = values.linkedObjectives;

        const isDelete = difference(value, objective);
        const isAdded = difference(objective, value);
        if (values.linkedObjectives) delete values.linkedObjectives;
        let dataSet = {
          ...values,
          projectId: activity.currentActivity.id,
        };
        if (isNew) {
          if (!isEmpty(isAdded)) {
            dataSet.linkedObjectives = isAdded;
          }

          dispatch(createProjectEvidence('evidence', dataSet));
        } else {
          values.id = evidence.id;
          if (!isEmpty(isAdded)) {
            dataSet.assignObjectives = isAdded;
          }

          if (!isEmpty(isDelete)) {
            dataSet.unAssignObjectives = isDelete;
          }

          dispatch(
            updateProjectEvidence(
              'evidence',
              activity.currentActivity.id,
              evidence.id,
              dataSet
            )
          );
        }
      }

      toggle();
    } catch (error) {
      console.error('EvidenceForm :: handleValidSubmit -> error', error);
    }
  }

  function getHelpMessageForType(typeId) {
    const extraEvidenceType = find(extra.evidenceTypes, {
      id: parseInt(typeId),
    });

    const helpMessage = extraEvidenceType
      ? find(helpTips, { title: extraEvidenceType.evidenceType })
      : null;

    return helpMessage ? helpMessage.content : '';
  }

  function handleEvidenceType(newEvidenceType, setFieldValue) {
    setEvidenceMessage(getHelpMessageForType(newEvidenceType));
    setEvidenceType(newEvidenceType);

    if (isNew && Number(newEvidenceType) === EVIDENCE_TYPE.PRACTICE_KNOWLEDGE) {
      const fullName = `${get(auth, 'user.firstName', '')} ${get(
        auth,
        'user.surname',
        ''
      )}`.trim();
      setFieldValue('authors', [fullName]);
      setFieldValue('year', new Date().getFullYear());
    }
  }

  const {
    evidence,
    extra,
    auth,
    helpTips,
    activity,
    isNew,
    disabled,
    isOwned,
    isReadOnly,
    isParent = false,
  } = props;

  let evidenceNew = cloneDeep(evidence);

  if (!isNew && !isParent) {
    let linkedObjectives = [];
    for (const item of evidence.objective) {
      linkedObjectives.push(item.id);
    }

    evidenceNew.linkedObjectives = linkedObjectives;
  }

  // Existing evidence already saved against the deactivated "Data" type
  // (Data is no longer offered) and none of the new structured fields apply.
  const isLegacyData =
    !isNew && isLegacyDataType(get(evidence, 'evidenceType'));

  const evidenceTypes = extra.evidenceTypes.map((item, key) => ({
    key,
    value: item.id,
    text: item.evidenceType,
  }));

  const evidenceTypeOptions = isLegacyData
    ? [
        ...evidenceTypes,
        {
          key: 'legacy-data',
          value: evidence.evidenceType,
          text: get(evidence, 'evidenceTypeData.evidenceType', 'Data'),
        },
      ]
    : evidenceTypes;

  const fieldConfig = EVIDENCE_TYPE_FIELD_CONFIG[Number(evidenceType)];

  // In view mode, an evidence record from before this metadata existed can
  // have all of it empty — showing a block of blank, disabled fields is just
  // noise, so hide the Material type/Title/Year, Author, and DOI/Link blocks
  // entirely when there's nothing in any of them to view.
  const hasOptionalMetadata =
    !!get(evidence, 'materialTypeId') ||
    !!get(evidence, 'title') ||
    !!get(evidence, 'year') ||
    !!get(evidence, 'author') ||
    !!get(evidence, 'doi') ||
    !!get(evidence, 'link');
  const showOptionalMetadata = !disabled || hasOptionalMetadata;

  const materialTypeOptions = (extra.evidenceMaterialTypes || []).map(
    (item, key) => ({
      key,
      value: item.id,
      text: item.name,
    })
  );

  const currentTypeLabel = isLegacyData
    ? get(evidence, 'evidenceTypeData.evidenceType', 'Data')
    : get(
        find(extra.evidenceTypes, { id: Number(evidenceType) }),
        'evidenceType',
        ''
      );
  const currentTypeIcon = fieldConfig
    ? fieldConfig.icon
    : 'file alternate outline';
  const stepEnterClass =
    stepDirection === 'back'
      ? 'evidence-step-enter-back'
      : 'evidence-step-enter-forward';
  const isStep2 = isLegacyData || !!fieldConfig;
  const stepsWrapperClass = `evidence-form-steps ${
    isStep2 ? 'evidence-form-steps--step2' : 'evidence-form-steps--step1'
  }`;

  useEffect(() => {
    if (typeof props.onStepChange === 'function') {
      props.onStepChange(isStep2);
    }
  }, [isStep2]);

  // We now default to all objectives selected...
  const initialFormValues = isParent
    ? evidenceInitialValues(evidenceNew, true)
    : isNew
      ? {
          ...evidenceInitialValues(evidence),
          linkedObjectives: map(
            get(activity, 'currentActivity.objective', []),
            (item) => item.id
          ),
        }
      : evidenceInitialValues(evidenceNew);

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={evidenceValidationSchema(t, isParent)}
      onSubmit={handleValidSubmit}
    >
      {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
        <Form onSubmit={handleSubmit}>
          {!isOwned ? (
            <div className="ui negative message mb-30">
              {t('activity.evidence.form.library_cannot_edit')}
            </div>
          ) : null}

          <div className={stepsWrapperClass}>
            {!isLegacyData && !fieldConfig ? (
              <div className={stepEnterClass}>
                <div className="evidence-type-tiles">
                  {evidenceTypeOptions.map((item) => (
                    <div
                      key={item.value}
                      className="evidence-type-tile"
                      onClick={() => {
                        if (disabled) return;
                        setStepDirection('forward');
                        setFieldValue('evidenceType', item.value);
                        handleEvidenceType(item.value, setFieldValue);
                      }}
                      onMouseEnter={() =>
                        setHoveredMessage(getHelpMessageForType(item.value))
                      }
                      onMouseLeave={() => setHoveredMessage('')}
                    >
                      <Icon
                        name={
                          get(
                            EVIDENCE_TYPE_FIELD_CONFIG[Number(item.value)],
                            'icon'
                          ) || 'file alternate outline'
                        }
                        className="evidence-type-tile-icon"
                      />
                      <div className="evidence-type-tile-label">
                        {item.text}
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  className={`evidence-type-hover-message${
                    hoveredMessage
                      ? ' evidence-type-hover-message--visible'
                      : ''
                  }`}
                >
                  {hoveredMessage}
                </div>
              </div>
            ) : (
              <div className={stepEnterClass}>
                {!isLegacyData && !disabled && (
                  <button
                    type="button"
                    className="evidence-change-type-link"
                    onClick={() => {
                      setStepDirection('back');
                      setFieldValue('evidenceType', '');
                      setEvidenceType(0);
                      setEvidenceMessage('');
                    }}
                  >
                    <Icon name="angle left" />
                    {t('activity.evidence.form.change_type')}
                  </button>
                )}

                <div className="evidence-step-header">
                  <Icon
                    name={currentTypeIcon}
                    className="evidence-step-header-icon"
                  />
                  <div className="evidence-step-header-label">
                    {currentTypeLabel}
                  </div>
                </div>

                {evidenceMessage ? <div>{evidenceMessage}</div> : null}

                {isLegacyData ? (
                  <>
                    <div className="my-2">
                      <CustomTextArea
                        id="content"
                        name="content"
                        placeholder={t(
                          'activity.evidence.form.content_placeholder'
                        )}
                        labelText={
                          <CustomTooltip
                            state={get(auth, 'user.options.tips')}
                            contentObject={{ data: helpTips, key: '29' }}
                            position="top left"
                            wide="very"
                          >
                            <span>
                              {disabled
                                ? t('common.evidence')
                                : t('activity.evidence.form.enter_evidence')}
                            </span>
                          </CustomTooltip>
                        }
                        disabled={disabled}
                        autoResize
                      />
                    </div>
                    <div className="my-2">
                      <CustomTextArea
                        id="source"
                        name="source"
                        placeholder={t(
                          'activity.evidence.form.source_placeholder'
                        )}
                        labelText={
                          <CustomTooltip
                            state={get(auth, 'user.options.tips')}
                            contentObject={{ data: helpTips, key: '30' }}
                            position="top left"
                            wide="very"
                          >
                            <span>{t('activity.evidence.form.source')}</span>
                          </CustomTooltip>
                        }
                        disabled={disabled}
                        autoResize
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {showOptionalMetadata &&
                      (fieldConfig.showTitle ||
                        fieldConfig.showYear ||
                        fieldConfig.showMaterialType) && (
                        <div className="my-2 evidence-inline-row">
                          {fieldConfig.showMaterialType && (
                            <div className="evidence-inline-col evidence-inline-col--material">
                              <CustomDropdown
                                selection
                                name="materialTypeId"
                                id="materialTypeId"
                                labelText={t(
                                  'activity.evidence.form.material_type'
                                )}
                                options={materialTypeOptions}
                                placeholder={t(
                                  'activity.evidence.form.material_type'
                                )}
                                disabled={disabled}
                                requiredStar
                              />
                            </div>
                          )}
                          {fieldConfig.showTitle && (
                            <div className="evidence-inline-col evidence-inline-col--grow">
                              <CustomInput
                                id="title"
                                name="title"
                                labelText={t('common.title')}
                                placeholder={t('common.title')}
                                disabled={disabled}
                                requiredStar
                              />
                            </div>
                          )}
                          {fieldConfig.showYear && (
                            <div className="evidence-inline-col evidence-inline-col--narrow">
                              <CustomInput
                                id="year"
                                name="year"
                                type="number"
                                labelText={t('date.year')}
                                placeholder={t('date.year')}
                                disabled={disabled}
                                requiredStar
                              />
                            </div>
                          )}
                        </div>
                      )}

                    {showOptionalMetadata && fieldConfig.showAuthor && (
                      <div className="my-2">
                        <label
                          className={
                            AUTHOR_REQUIRED_TYPES.includes(Number(evidenceType))
                              ? 'required-stars'
                              : ''
                          }
                        >
                          {t('activity.evidence.form.author')}
                        </label>
                        <FieldArray name="authors">
                          {({ push, remove, form }) => {
                            const authors = get(form.values, 'authors', ['']);
                            const authorsError =
                              form.submitCount > 0 ? form.errors.authors : null;
                            return (
                              <div className="evidence-author-list">
                                {authors.map((_, index) => (
                                  <div
                                    className="evidence-author-row"
                                    key={index}
                                  >
                                    <Field
                                      name={`authors[${index}]`}
                                      placeholder={t(
                                        'activity.evidence.form.author'
                                      )}
                                      disabled={disabled}
                                      className="evidence-author-input"
                                    />
                                    {authors.length > 1 && (
                                      <button
                                        type="button"
                                        className="evidence-author-remove"
                                        onClick={() => remove(index)}
                                        disabled={disabled}
                                        aria-label={t(
                                          'activity.evidence.form.remove_author'
                                        )}
                                      >
                                        <Icon name="close" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                {!disabled && (
                                  <button
                                    type="button"
                                    className="evidence-add-author"
                                    onClick={() => push('')}
                                  >
                                    <Icon name="plus" />{' '}
                                    {t('activity.evidence.form.add_author')}
                                  </button>
                                )}
                                {authorsError ? (
                                  <div className="invalid-feedback">
                                    {authorsError}
                                  </div>
                                ) : null}
                              </div>
                            );
                          }}
                        </FieldArray>
                      </div>
                    )}

                    {showOptionalMetadata &&
                      (fieldConfig.showDoi || fieldConfig.showLink) && (
                        <div className="my-2 evidence-inline-row">
                          {fieldConfig.showDoi && (
                            <div className="evidence-inline-col evidence-inline-col--doi">
                              <CustomInput
                                id="doi"
                                name="doi"
                                labelText={t('activity.evidence.form.doi')}
                                placeholder={t(
                                  'activity.evidence.form.doi_placeholder'
                                )}
                                disabled={disabled}
                              />
                            </div>
                          )}
                          {fieldConfig.showLink && (
                            <div className="evidence-inline-col evidence-inline-col--grow">
                              <CustomInput
                                id="link"
                                name="link"
                                labelText={t('activity.evidence.form.link')}
                                placeholder={t(
                                  'activity.evidence.form.link_placeholder'
                                )}
                                disabled={disabled}
                                requiredStar={LINK_REQUIRED_TYPES.includes(
                                  Number(evidenceType)
                                )}
                              />
                            </div>
                          )}
                        </div>
                      )}

                    <div className="my-2">
                      <CustomTextArea
                        id="content"
                        name="content"
                        placeholder={fieldConfig.contentPlaceholder}
                        labelText={fieldConfig.contentLabel}
                        disabled={disabled}
                        requiredStar
                        autoResize
                      />
                    </div>

                    {isEmpty(get(evidence, 'title')) &&
                      !isEmpty(get(evidence, 'source')) && (
                        <div className="my-2">
                          <CustomTextArea
                            id="source"
                            name="source"
                            labelText={t('activity.evidence.source_label')}
                            disabled={disabled}
                            autoResize
                          />
                        </div>
                      )}

                    {fieldConfig.showVisibilityToggle && (
                      <div className="my-2 evidence-visibility-toggle">
                        <div className="evidence-visibility-toggle-header group-toggle-switch">
                          <Radio
                            toggle
                            checked={!!values.visibleOnPlatform}
                            disabled={disabled}
                            onChange={(e, { checked }) =>
                              setFieldValue('visibleOnPlatform', checked)
                            }
                          />
                          <label>
                            {t('activity.evidence.allow_in_all_takso')}
                          </label>
                        </div>
                        <p>{t('activity.evidence.visibility_usage')}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {isStep2 && (
            <>
              {!isParent && (
                <div className="evidence-objectives-list">
                  <CheckboxComp
                    labelText={t('activity.evidence.form.relevant_objectives')}
                    options={options}
                    id="linkedObjectives"
                    name="linkedObjectives"
                    setFieldValue={setFieldValue}
                    disabled={!isReadOnly && !isOwned ? false : disabled}
                  />
                </div>
              )}

              <div className="model-btn-align-right">
                {!disabled && (
                  <Button
                    content={isNew ? t('actions.save') : t('actions.update')}
                    type="submit"
                    className="Primary-Button"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  />
                )}
                <Button
                  content={disabled ? t('actions.close') : t('actions.cancel')}
                  type="button"
                  className="Secondary-Button"
                  disabled={isSubmitting}
                  onClick={props.toggle}
                />
              </div>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

class EvidenceDelete extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      modal: false,
    };
  }

  toggle() {
    this.setState((prevState) => ({
      modal: !prevState.modal,
    }));
  }

  async deleteOrgContractorHandle() {
    try {
      const { deleteObjectiveEvidence, evidence, activity } = this.props;
      deleteObjectiveEvidence(
        'evidence',
        activity.currentActivity.id,
        evidence.id
      );
      this.toggle();
    } catch (error) {
      console.error(
        'EvidenceDelete :: deleteOrgContractorHandle -> error',
        error
      );
    }
  }

  render() {
    const { t } = this.props;
    const { modal } = this.state;
    return (
      <Modal
        open={modal}
        onOpen={() => this.toggle()}
        onClose={() => this.toggle()}
        size="mini"
        trigger={
          <div
            className="remove-evidence"
            title={t('activity.evidence.form.remove_evidence')}
          >
            <Icon name="trash alternate" />
          </div>
        }
      >
        <Modal.Header>
          {t('activity.evidence.form.remove_evidence')}
        </Modal.Header>
        <Modal.Content>
          <p>{t('activity.evidence.form.remove_evidence_confirm')}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => this.toggle()} className="modal-cancel">
            {t('actions.no')}
          </Button>
          <Button
            onClick={() => this.deleteOrgContractorHandle()}
            className="modal-proceed"
            content={t('actions.yes')}
          />
        </Modal.Actions>
      </Modal>
    );
  }
}

EvidenceDelete = withTranslation()(EvidenceDelete);

class EvidenceEdit extends PureComponent {
  constructor(props) {
    super(props);
    // Computed up front (rather than left to default to false and waiting
    // on EvidenceForm's onStepChange callback) so the modal opens at its
    // final 75vw width immediately for an existing record, instead of
    // visibly transitioning from 50vw right after it appears.
    const { evidence } = props;
    const fieldConfig = evidence
      ? EVIDENCE_TYPE_FIELD_CONFIG[Number(evidence.evidenceType)]
      : null;
    const isLegacyData = !!evidence && isLegacyDataType(evidence.evidenceType);
    this.state = {
      modal: false,
      isStep2: isLegacyData || !!fieldConfig,
    };
  }

  toggle() {
    this.setState((prevState) => ({
      modal: !prevState.modal,
    }));
  }

  render() {
    const { t } = this.props;
    const { modal, isStep2 } = this.state;
    const { evidence, activity, isReadOnly } = this.props;

    let isOwned = false;
    if (evidence.createdByProjectId === activity.currentActivity.id) {
      isOwned = true;
    }

    let disabled = false;
    if (!isOwned || isReadOnly) {
      disabled = true;
    }

    const modalClassName = `evidence-modal-responsive ${
      isStep2 ? 'evidence-modal-responsive--step2' : ''
    }`;

    return (
      <Modal
        trigger={
          <div className="label-container" onClick={() => this.toggle()}>
            {disabled
              ? t('activity.evidence.form.view_evidence')
              : t('specific_actions.edit_evidence')}
          </div>
        }
        size="small"
        open={modal}
        className={modalClassName}
      >
        <Modal.Header className="goal-confirm-modal-header">
          <div className="modal-heading">
            {' '}
            {disabled
              ? t('activity.evidence.form.view_evidence')
              : t('specific_actions.edit_evidence')}
          </div>
          <div className="slim-close-icon" onClick={() => this.toggle()}>
            &times;
          </div>
        </Modal.Header>

        <Modal.Content>
          <EvidenceForm
            toggle={() => this.toggle()}
            isNew={false}
            disabled={disabled}
            isReadOnly={isReadOnly}
            isOwned={isOwned}
            onStepChange={(value) => this.setState({ isStep2: value })}
            {...this.props}
          />
        </Modal.Content>
      </Modal>
    );
  }
}

EvidenceEdit = withTranslation()(EvidenceEdit);

class EvidenceCreate extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      modal: false,
      isStep2: false,
    };
  }

  toggle() {
    this.setState((prevState) => ({
      modal: !prevState.modal,
    }));
  }

  render() {
    const { t } = this.props;
    const { modal, isStep2 } = this.state;
    const { auth, helpTips } = this.props;

    const modalClassName = `create-new-evidence evidence-modal-responsive ${
      isStep2 ? 'evidence-modal-responsive--step2' : ''
    }`;

    return (
      <div>
        <Modal
          trigger={
            <div>
              <CustomTooltip
                state={get(auth, 'user.options.tips')}
                contentObject={{ data: helpTips, key: '24' }}
                position="bottom left"
                wide="very"
              >
                <div
                  onClick={() => this.toggle()}
                  className="Secondary-Button "
                >
                  <Icon name="plus" />
                  {t('activity.evidence.form.add_new_evidence')}
                </div>
              </CustomTooltip>
            </div>
          }
          size="small"
          open={modal}
          className={modalClassName}
        >
          <Modal.Header className="goal-confirm-modal-header">
            <div className="modal-heading">
              {t('activity.evidence.form.add_new_evidence')}
            </div>
            <div className="slim-close-icon" onClick={() => this.toggle()}>
              &times;
            </div>
          </Modal.Header>
          <Modal.Content>
            <EvidenceForm
              toggle={() => this.toggle()}
              isNew={true}
              isOwned={true}
              onStepChange={(value) => this.setState({ isStep2: value })}
              {...this.props}
            />
          </Modal.Content>
        </Modal>
      </div>
    );
  }
}

EvidenceCreate = withTranslation()(EvidenceCreate);

class EvidenceAssign extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {};
  }

  render() {
    const { evidence } = this.props;
    return (
      <div className="evidence-container">
        {map(evidence.objective, (item, i) => {
          return (
            <label
              className="evidence-objective-label"
              key={i}
            >{`${item.outcome.title}`}</label>
          );
        })}
      </div>
    );
  }
}

const noObjective = (t) => (
  <Alert color="warning">{t('activity.evidence.select_an_objective')}</Alert>
);

let ActivityGoalsOverview = ({ t, auth, activity, currentState }) => {
  const { currentActivity } = activity;
  const objective = currentActivity.objective || [];
  const form = {
    goal: currentActivity.goal ? (
      currentActivity.goal
    ) : (
      <Alert color="warning">{t('activity.evidence.select_a_goal')}</Alert>
    ),
    description: currentActivity.goalDescription
      ? currentActivity.goalDescription
      : '-',
  };

  return (
    <Segment
      className="content-segment objective evidence-goals-overview"
      disabled={getPageDisable(currentActivity, auth.user, currentState)}
    >
      <div className="evidence-goals-overview-goal">
        <label className="goal-label">{t('common.goal')}:</label>
        <div className="goal-title-single-evidence">{form.goal}</div>
        <label className="goal-label">
          {t('activity.evidence.goal_description')}:
        </label>
        <ShowMoreText
          lines={3}
          more={t('common.read_more')}
          less={t('common.read_less')}
          anchorClass="show-more-less-clickable"
          expanded={false}
          truncatedEndingComponent={'... '}
          className="pre-line evidence-goals-overview-goal-description"
        >
          <label>{form.description}</label>
        </ShowMoreText>
      </div>

      <div className="evidence-goals-overview-objectives">
        <label className="goal-label">
          {t('activity.evidence.selected_objectives')}:
        </label>
        {isEmpty(objective) ? (
          noObjective(t)
        ) : (
          <ul className="evidence-goals-overview-objectives-list">
            {map(objective, (obj, i) => (
              <li key={obj.id || i}>
                {obj.outcome ? obj.outcome.title : noObjective(t)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Segment>
  );
};

ActivityGoalsOverview = connect((state) => ({
  auth: state.authentication,
  activity: state.activity,
}))(withTranslation()(ActivityGoalsOverview));

class ActivityEvidence extends PureComponent {
  state = {
    libraryOpen: false,
  };

  toggleLibrary = () => {
    this.setState((prevState) => ({ libraryOpen: !prevState.libraryOpen }));
  };

  columns() {
    const { t, currentState, auth, activity } = this.props;
    const { currentActivity } = activity;
    let disabled = getPageDisable(currentActivity, auth.user, currentState);

    return [
      {
        title: t('common.title'),
        width: 6,
        render: (data) => {
          const displayTitle = getEvidenceDisplayTitle(data, 60);

          return (
            <div>
              <label className="evidence-mainText">
                {data.year ? `${displayTitle} (${data.year})` : displayTitle}
              </label>
            </div>
          );
        },
      },
      {
        title: t('activity.evidence.form.author'),
        width: 2,
        render: (data) => {
          const authors = (data.author || '')
            .split(';')
            .map((name) => name.trim())
            .filter(Boolean);

          return (
            <div>
              {isEmpty(authors) ? (
                <label className="evidence-mainText">-</label>
              ) : (
                authors.map((name, i) => (
                  <label
                    key={i}
                    className="evidence-mainText evidence-author-line"
                  >
                    {name}
                  </label>
                ))
              )}
            </div>
          );
        },
      },
      {
        title: t('activity.evidence.form.material_type'),
        width: 3,
        render: (data) => {
          return (
            <div>
              <label className="evidence-mainText">
                {getEvidenceTypeLine(data) || '-'}
              </label>
            </div>
          );
        },
      },
      {
        title: t('activity.evidence.form.linked_objectives'),
        width: 4,
        render: (data) => {
          return (
            <div className="objective-container-evidence">
              <EvidenceAssign key={data.id} evidence={data} {...this.props} />
            </div>
          );
        },
        with: '500',
      },
      {
        title: '',
        width: 2,
        render: (data) => {
          return (
            <div>
              <Segment>
                <EvidenceEdit
                  evidence={data}
                  {...this.props}
                  isReadOnly={disabled}
                />
              </Segment>
            </div>
          );
        },
      },
      {
        title: '',
        width: 1,
        textAlign: 'right',
        render: (data) => {
          return (
            <div>
              <Segment disabled={disabled} className="evidence-remove-button">
                <EvidenceDelete evidence={data} {...this.props} />
              </Segment>
            </div>
          );
        },
      },
    ];
  }

  createEvidenceList() {
    const { currentActivity } = this.props.activity;
    if (currentActivity.objective) {
      const result = [];

      forEach(cloneDeep(currentActivity.objective), (objective, i) => {
        forEach(objective.evidence, (evidence) => {
          const index = findIndex(result, { id: evidence.id });
          if (index > -1) {
            result[index].objective.push(objective);
          } else {
            evidence.objective = [objective];
            result.push(evidence);
          }
        });
      });
      return result;
    }
    return [];
  }

  render() {
    const { libraryOpen } = this.state;

    const { t, auth, activity, currentState } = this.props;

    const { currentActivity } = activity;

    const evidence = this.createEvidenceList();

    return (
      <>
        <Segment
          className="evidence-section-segment"
          disabled={getPageDisable(currentActivity, auth.user, currentState)}
        >
          <div className="evidence-section-header-row">
            <p className="evidence-section-description">
              {t('activity.evidence.evidence_section_description')}
            </p>
            <div className="evidence-actions-inline">
              <EvidenceCreate {...this.props} />
              <div className="Secondary-Button" onClick={this.toggleLibrary}>
                <Icon name="book" />
                {t('activity.evidence.evidence_library')}
              </div>
            </div>
          </div>

          <SlideoutPanel
            open={libraryOpen}
            onClose={this.toggleLibrary}
            title={t('activity.evidence.evidence_library')}
            wide="library"
            className="dialog-width-xl"
          >
            {libraryOpen ? <EvidenceLibrary {...this.props} /> : null}
          </SlideoutPanel>
        </Segment>
        <Segment className="evidence-table-segment">
          <CustomTable
            header
            columns={this.columns()}
            data={evidence}
            height={true}
            emptyMessage={t('activity.evidence.no_evidence_added')}
            customClass="evidence-table-new"
            keyExtractor={(item, index) => {
              return item.evidenceId;
            }}
          />
        </Segment>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    auth: state.authentication,
    activity: state.activity,
    user: state.user,
    extra: state.extra,
    helpTips: state.extra.activityHelpTips,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getEvidenceTypes,
      getEvidenceMaterialTypes,
      createProjectEvidence,
      updateProjectEvidence,
      deleteObjectiveEvidence,
    },
    dispatch
  );
};

ActivityEvidence = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ActivityEvidence));

export {
  ActivityEvidence,
  ActivityGoalsOverview,
  EvidenceCreate,
  EvidenceEdit,
  EvidenceDelete,
};
